import React, { useEffect, useMemo, useRef, useState } from 'react';
import MoreMenu, { swatch } from './MoreMenu';
import { NameForm } from './TodoSidebar';
import { Avatar } from './TodoPeople';
import { PinIcon } from './TodoNotes';
import { Icon } from './icons';
import { isNote, pinnedFirst } from './notes';
import { PartsChips, partsOf } from './TodoParts';
import { dueChip } from '../../lib/when';
import { dueState } from '../todoViews';
import './TodoBoard.css';

const START_AFTER = 4;   // px of movement before a press on a grip becomes a drag
const EDGE = 60;         // px from an edge where dragging scrolls

/**
 * THE BOARD (batch 5): a list as Trello's columns, which Todoist offers on
 * every project beside the List. Each list remembers which of the two it is
 * shown as - on the server, so it is the same on the phone - and the toggle
 * is on the list's header.
 *
 * The COLUMNS are the list's sections, "No section" first; the CARDS are its
 * to-dos and notes. A sub-task is not a card: its parent says "1/3 ▸"
 * instead, and a press on that - or on the card's title - opens the to-do
 * in the side panel (TodoPanel.js), where the sub-tasks are a tree to tick and add to.
 * A card is the row, smaller: the round checkbox in its
 * priority's colour, the title, its labels by name (two, then "+n"), and a
 * meta line - when it is due, its sub-tasks, its comments and, at the
 * right-hand end, whose it is; a note is its coloured card.
 *
 * DRAGGING is the tree's: Pointer Events, so a mouse and a finger are one
 * code path, from the ⠿ on a card (to another place, in its column or
 * another) or on a column's heading (to reorder the columns). Near the edge
 * of the board it scrolls sideways by itself, near the top or bottom of the
 * window the page does. Without a pointer, a card's ⋯ has "Move to column…",
 * and a column's ⋯ moves it left and right.
 *
 * ON A PHONE the columns are most of the screen wide and snap into place as
 * the board is swiped across - inside the board's own box: the page itself
 * never scrolls sideways.
 *
 * The page does every call; `on` says what was asked for.
 */
export default function TodoBoard({ list, sections, items, folded, doneUnder, labelById, now, todayIso,
                                    busy, selfKey, on, renderNote, cardMenu }) {
  const boardRef = useRef(null);
  const dragRef = useRef(null);
  const [live, setLive] = useState(false);
  const [drag, setDrag] = useState(null);     // { type, id, x, y, width, title }
  const [drop, setDrop] = useState(null);     // card: { col, index }; column: { index }
  const [adding, setAdding] = useState(null); // column key getting a card
  const [text, setText] = useState('');
  const [addingCol, setAddingCol] = useState(false);
  const [renaming, setRenaming] = useState(null);

  const columns = useMemo(() => {
    const known = new Set(sections.map((s) => s.id));
    const byId = new Map(items.map((t) => [t.id, t]));
    const roots = items.filter((t) => !t.parent_id || !byId.has(t.parent_id));
    const kids = new Map();
    for (const t of items) if (t.parent_id && byId.has(t.parent_id)) kids.set(t.parent_id, (kids.get(t.parent_id) || 0) + 1);
    const col = (key, s) => ({
      key,
      section: s,
      name: s ? s.name : 'No section',
      folded: Boolean(s) && folded.has(s.id),
      cards: roots.filter((t) => (s ? t.section_id === s.id : !t.section_id || !known.has(t.section_id)))
        .sort(pinnedFirst),
      kids
    });
    return [col('_', null), ...sections.map((s) => col(s.id, s))];
  }, [sections, items, folded]);

  /* --------------------------------------------------------------- dragging */

  const startCard = (e, item, colKey) => {
    if (busy || dragRef.current) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const el = e.currentTarget.closest('[data-card]');
    const r = el.getBoundingClientRect();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (err) { /* synthetic */ }
    dragRef.current = { type: 'card', id: item.id, item, from: colKey, pointerId: e.pointerId,
                        x0: e.clientX, y0: e.clientY, x: e.clientX, y: e.clientY,
                        offX: e.clientX - r.left, offY: e.clientY - r.top, width: r.width,
                        title: item.title || 'Note', started: false, hit: null, raf: 0 };
    setLive(true);
  };
  const startCol = (e, at, s) => {
    if (busy || dragRef.current) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const el = e.currentTarget.closest('[data-col]');
    const r = el.getBoundingClientRect();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (err) { /* synthetic */ }
    dragRef.current = { type: 'col', id: s.id, from: at, pointerId: e.pointerId,
                        x0: e.clientX, y0: e.clientY, x: e.clientX, y: e.clientY,
                        offX: e.clientX - r.left, offY: e.clientY - r.top, width: r.width,
                        title: s.name, started: false, hit: null, raf: 0 };
    setLive(true);
  };

  const onRef = useRef(on);
  onRef.current = on;
  const colsRef = useRef(columns);
  colsRef.current = columns;

  useEffect(() => {
    if (!live) return undefined;
    const box = () => boardRef.current;

    const locate = () => {
      const d = dragRef.current;
      const b = box();
      if (!d || !b) return null;
      const cols = [...b.querySelectorAll('[data-col]')];
      if (d.type === 'col') {
        // Among the named columns other than the one moving: how many are left of the pointer.
        const others = cols.filter((el) => el.dataset.col !== '_' && el.dataset.col !== d.id);
        const index = others.filter((el) => { const r = el.getBoundingClientRect(); return r.left + r.width / 2 < d.x; }).length;
        return { index };
      }
      let best = null;
      let dist = Infinity;
      for (const el of cols) {
        const r = el.getBoundingClientRect();
        const dd = d.x < r.left ? r.left - d.x : d.x > r.right ? d.x - r.right : 0;
        if (dd < dist) { dist = dd; best = el; }
      }
      if (!best) return null;
      const key = best.dataset.col;
      const cards = [...best.querySelectorAll('[data-card]')].filter((el) => el.dataset.card !== String(d.id));
      const index = cards.filter((el) => { const r = el.getBoundingClientRect(); return r.top + r.height / 2 < d.y; }).length;
      return { col: key, index };
    };

    const update = () => {
      const d = dragRef.current;
      if (!d || !d.started) return;
      d.hit = locate();
      setDrop(d.hit);
    };

    const scrollLoop = () => {
      const d = dragRef.current;
      const b = box();
      if (!d || !d.started || !b) return;
      const r = b.getBoundingClientRect();
      const sx = d.x < r.left + EDGE ? -Math.ceil((r.left + EDGE - d.x) / 4)
        : d.x > r.right - EDGE ? Math.ceil((d.x - (r.right - EDGE)) / 4) : 0;
      const h = window.innerHeight;
      const sy = d.y < EDGE ? -Math.ceil((EDGE - d.y) / 4) : d.y > h - EDGE ? Math.ceil((d.y - (h - EDGE)) / 4) : 0;
      if (!sx && !sy) { d.raf = 0; return; }
      if (sx) b.scrollLeft += sx;
      if (sy) window.scrollBy(0, sy);
      update();
      d.raf = requestAnimationFrame(scrollLoop);
    };

    const finish = (commit) => {
      const d = dragRef.current;
      dragRef.current = null;
      if (d?.raf) cancelAnimationFrame(d.raf);
      document.body.classList.remove('ctd-dragging');
      setDrag(null);
      setDrop(null);
      setLive(false);
      if (!commit || !d || !d.started || !d.hit) return;
      if (d.type === 'col') {
        if (d.hit.index !== d.from) onRef.current.moveColumn(d.from, d.hit.index);
        return;
      }
      const target = colsRef.current.find((c) => c.key === d.hit.col);
      if (!target) return;
      const shown = target.cards.filter((t) => t.id !== d.id);
      const here = colsRef.current.find((c) => c.key === d.from);
      const was = here ? here.cards.findIndex((t) => t.id === d.id) : -1;
      if (target.key === d.from && was === d.hit.index) return;
      onRef.current.moveCard(d.item, target.section ? target.section.id : null, d.hit.index, shown);
    };

    const onMove = (e) => {
      const d = dragRef.current;
      if (!d || e.pointerId !== d.pointerId) return;
      d.x = e.clientX;
      d.y = e.clientY;
      if (!d.started) {
        if (Math.hypot(d.x - d.x0, d.y - d.y0) < START_AFTER) return;
        d.started = true;
        document.body.classList.add('ctd-dragging');
      }
      e.preventDefault();
      setDrag({ type: d.type, id: d.id, title: d.title, x: d.x - d.offX, y: d.y - d.offY, width: d.width });
      update();
      const b = box();
      const r = b ? b.getBoundingClientRect() : null;
      if (!d.raf && r && (d.x < r.left + EDGE || d.x > r.right - EDGE || d.y < EDGE || d.y > window.innerHeight - EDGE)) {
        d.raf = requestAnimationFrame(scrollLoop);
      }
    };
    const onUp = (e) => { const d = dragRef.current; if (d && e.pointerId === d.pointerId) finish(true); };
    const onCancel = (e) => { const d = dragRef.current; if (d && e.pointerId === d.pointerId) finish(false); };
    const onKey = (e) => { if (e.key === 'Escape') finish(false); };
    window.addEventListener('pointermove', onMove, { passive: false });
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
      window.removeEventListener('keydown', onKey);
    };
  }, [live]);

  useEffect(() => () => document.body.classList.remove('ctd-dragging'), []);

  /*
   * THE SCROLL HINT. When there are more columns than fit, the edge they are
   * hiding behind fades out (a mask in TodoBoard.css), so a cut-off column
   * reads as "there is more this way" rather than as a fault - Trello's
   * board does the same. Which edges fade is worked out here, from where
   * the board is scrolled to and how wide it is.
   */
  const [more, setMore] = useState({ left: false, right: false });
  useEffect(() => {
    const b = boardRef.current;
    if (!b) return undefined;
    const look = () => {
      const left = b.scrollLeft > 2;
      const right = b.scrollLeft + b.clientWidth < b.scrollWidth - 2;
      setMore((m) => (m.left === left && m.right === right ? m : { left, right }));
    };
    look();
    b.addEventListener('scroll', look, { passive: true });
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(look);
    if (ro) { ro.observe(b); if (b.firstElementChild) ro.observe(b.firstElementChild); }
    return () => { b.removeEventListener('scroll', look); if (ro) ro.disconnect(); };
  }, []);

  /* ------------------------------------------------------------------ cards */

  const named = columns.filter((c) => c.section);

  const taskCard = (item, col) => {
    const p = Number(item.priority) || 4;
    const when = item.due_date ? dueChip(String(item.due_date).slice(0, 10), item.due_time, now) : null;
    const state = dueState(item, todayIso);
    const tags = (item.labels || []).map((id) => labelById.get(id)).filter(Boolean);
    const talk = Number(item.comment_count) || 0;
    const kidsOpen = col.kids.get(item.id) || 0;
    const kidsDone = doneUnder.get(item.id) || 0;
    // Its checklist and files (a note made a to-do keeps both): "2/3", and a count.
    const parts = partsOf(item);
    const hasParts = parts.lines > 0 || parts.files > 0;
    return (
      <>
        <label className={`ctd-check p${p}`} title={`Done${p < 4 ? ` · P${p}` : ''}`}>
          <input type="checkbox" checked={false} disabled={busy} onChange={() => on.complete(item)}
                 aria-label={`Mark "${item.title}" as done`} />
          <span className="ctd-ring" aria-hidden="true" />
        </label>
        <div className="tdb-card-body">
          <button type="button" className="tdb-card-title" onClick={() => on.open(item)}>{item.title}</button>
          {/* Labels by name, as Todoist's board card has them: two, then "+n". */}
          {tags.length > 0 && (
            <span className="tdb-tags" aria-label={`Labels: ${tags.map((b) => b.name).join(', ')}`}>
              {tags.slice(0, 2).map((b) => (
                <span key={b.id} className="tda-tag" data-label-chip={b.id}>
                  <span className={`tda-dot ${swatch(b.color)}`} aria-hidden="true" /><span className="tdb-tag-name">{b.name}</span>
                </span>
              ))}
              {tags.length > 2 && (
                <span className="tdb-tag-more" title={tags.slice(2).map((b) => b.name).join(', ')}>+{tags.length - 2}</span>
              )}
            </span>
          )}
          {(when || talk || kidsOpen + kidsDone > 0 || item.pinned || item.assignee_key || hasParts) && (
            <span className="tdb-card-meta">
              {item.pinned && <span className="tdb-pin" title="Pinned" aria-label="Pinned"><PinIcon on /></span>}
              {when && <span className={`ctd-due is-${state}`}><Icon name="clock" size={12} className="ctd-due-icon" />{when}</span>}
              {/* "2/5 ▸": the sub-tasks are not cards, so this opens the
                  side panel straight onto them (TodoPanel.js). */}
              {kidsOpen + kidsDone > 0 && (
                <button type="button" className="ctd-count tdb-subs" data-subtasks={`${kidsDone}/${kidsDone + kidsOpen}`}
                        title={`${kidsDone} of ${kidsDone + kidsOpen} sub-tasks done - show them`}
                        aria-label={`${kidsDone} of ${kidsDone + kidsOpen} sub-tasks done. Show the sub-tasks of "${item.title}"`}
                        onClick={() => on.openSubs(item)}>
                  {kidsDone}/{kidsDone + kidsOpen}<Icon name="chevronRight" size={11} className="tdb-subs-ic" />
                </button>
              )}
              {hasParts && <PartsChips item={item} onOpen={() => on.open(item)} />}
              {talk > 0 && (
                <span className="tdb-talk" data-comments={talk} title={talk === 1 ? '1 comment' : `${talk} comments`}>
                  <Icon name="comment" size={13} />{talk}
                </span>
              )}
              {/* Whose it is: at the right-hand end of the meta line, so the
                  title has the card's whole width. */}
              {item.assignee_key && (
                <span className="tds-holder" data-assignee={item.assignee_key}>
                  <Avatar name={item.assignee_name} who={item.assignee_key}
                          label={`Assigned to ${item.assignee_key === selfKey ? 'you' : item.assignee_name || 'somebody'}`} />
                </span>
              )}
            </span>
          )}
        </div>
      </>
    );
  };

  const card = (item, col) => {
    const note = isNote(item);
    const menu = (
      <MoreMenu label={`More for card "${item.title || 'note'}"`} disabled={busy}
                items={[{ label: 'Move to column…', onClick: () => on.askColumn(item) }, ...(cardMenu(item) || [])]} />
    );
    return (
      <React.Fragment key={item.id}>
        {drop && drop.col === col.key && drag?.type === 'card' && dropIndexIs(col, item, drop.index)
          && <div className="tdb-drop" aria-hidden="true" />}
        <div className={`tdb-card${note ? ' is-note' : ' is-task'}${item.color ? ` has-color ${swatch(item.color)}` : ''}${drag?.id === item.id ? ' is-moving' : ''}`}
             data-card={item.id} data-kind={note ? 'note' : 'task'}>
          <span className="tdb-grip" aria-hidden="true" title="Drag to move"
                onPointerDown={(e) => startCard(e, item, col.key)}><Icon name="grip" /></span>
          {note ? renderNote(item) : taskCard(item, col)}
          {menu}
        </div>
      </React.Fragment>
    );
  };

  // Where the line goes: before the card that is `index`-th among the others.
  const dropIndexIs = (col, item, index) => {
    const others = col.cards.filter((t) => t.id !== drag?.id);
    return others[index]?.id === item.id;
  };

  const submitCard = (e, col) => {
    e.preventDefault();
    const t = text.trim();
    if (!t) return;
    setText('');
    on.addCard(col.section ? col.section.id : null, t);
  };

  // A press on an edge's arrow: one column's width that way.
  const nudge = (dir) => {
    const b = boardRef.current;
    if (!b) return;
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    b.scrollBy({ left: dir * 268, behavior: reduce ? 'auto' : 'smooth' });
  };

  return (
    <div className="tdb-frame">
    {more.left && !drag && (
      <button type="button" className="tdb-nudge is-left" aria-label="Scroll the board left" title="Scroll left"
              tabIndex={-1} onClick={() => nudge(-1)}><Icon name="chevronRight" size={18} /></button>
    )}
    {more.right && !drag && (
      <button type="button" className="tdb-nudge is-right" aria-label="Scroll the board right" title="More columns"
              tabIndex={-1} onClick={() => nudge(1)}><Icon name="chevronRight" size={18} /></button>
    )}
    <div className={`tdb${drag ? ' is-dragging' : ''}${more.left ? ' has-more-left' : ''}${more.right ? ' has-more-right' : ''}`}
         ref={boardRef} data-board={list.id}
         aria-label={`${list.name} as a board`} role="region">
      <div className="tdb-cols">
        {columns.map((col) => {
          const at = col.section ? named.indexOf(col) : -1;
          const markL = drop && drag?.type === 'col' && col.section && drop.index === namedOthersIndex(named, drag.id, col.section.id);
          const others = col.cards.filter((t) => t.id !== drag?.id);
          return (
            <section key={col.key} data-col={col.key}
                     className={`tdb-col${col.folded ? ' is-folded' : ''}${drag?.type === 'col' && drag.id === col.key ? ' is-moving' : ''}${markL ? ' is-drop-before' : ''}${drop && drag?.type === 'col' && col.section && isLastDrop(named, drag.id, col.section.id, drop.index) ? ' is-drop-after' : ''}`}
                     aria-label={`${col.name}, ${col.cards.length}`}>
              <header className="tdb-col-head">
                {/* "No section" cannot move, but keeps the grip's room, so
                    every column's name starts at the same place. */}
                {col.section ? (
                  <span className="tdb-grip is-col" aria-hidden="true" title="Drag to reorder columns"
                        onPointerDown={(e) => startCol(e, at, col.section)}><Icon name="grip" /></span>
                ) : <span className="tdb-grip is-col is-gap" aria-hidden="true" />}
                {renaming === col.key ? (
                  <NameForm label={`New name for column "${col.name}"`} initial={col.name} submit="Save"
                            className="tda-rename-form"
                            onSave={(n) => { setRenaming(null); on.renameColumn(col.section, n); }}
                            onCancel={() => setRenaming(null)} />
                ) : (
                  <h3 className="tdb-col-name">
                    {col.folded ? (
                      <button type="button" className="tdb-unfold" aria-expanded="false"
                              aria-label={`Show column "${col.name}"`}
                              onClick={() => on.foldColumn(col.section.id, false)}>{col.name}</button>
                    ) : col.name}
                    <span className="tdb-col-n">{col.cards.length}</span>
                  </h3>
                )}
                {col.section && (
                  <MoreMenu label={`More for column "${col.name}"`} disabled={busy} items={[
                    { label: 'Rename', onClick: () => setRenaming(col.key) },
                    { label: col.folded ? 'Expand' : 'Collapse', onClick: () => on.foldColumn(col.section.id, !col.folded) },
                    at > 0 && { label: 'Move left', onClick: () => on.moveColumn(at, at - 1) },
                    at < named.length - 1 && { label: 'Move right', onClick: () => on.moveColumn(at, at + 1) },
                    { label: 'Delete…', danger: true, onClick: () => on.deleteColumn(col.section) }
                  ]} />
                )}
              </header>
              {!col.folded && (
                <>
                  <div className="tdb-cards" aria-label={`Cards in ${col.name}`}>
                    {col.cards.map((item) => card(item, col))}
                    {drop && drag?.type === 'card' && drop.col === col.key && drop.index >= others.length
                      && <div className="tdb-drop" aria-hidden="true" />}
                    {!col.cards.length && !(drop && drop.col === col.key) && (
                      <p className="tdb-empty">Nothing here yet.</p>
                    )}
                  </div>
                  {adding === col.key ? (
                    <form className="tdb-add-form" onSubmit={(e) => submitCard(e, col)}
                          onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); setAdding(null); setText(''); } }}>
                      <input type="text" value={text} autoFocus maxLength={300} aria-label={`New card in ${col.name}`}
                             placeholder='e.g. "Order cable fri p1"' onChange={(e) => setText(e.target.value)} />
                      <span className="tda-name-buttons">
                        <button type="submit" className="btn btn-primary btn-small" disabled={busy || !text.trim()}>Add</button>
                        <button type="button" className="btn btn-secondary btn-small"
                                onClick={() => { setAdding(null); setText(''); }}>Cancel</button>
                      </span>
                    </form>
                  ) : (
                    <button type="button" className="tda-add-line tdb-add-card" disabled={busy}
                            onClick={() => { setAdding(col.key); setText(''); }}>+ Add a card</button>
                  )}
                </>
              )}
            </section>
          );
        })}
        <div className="tdb-col is-new">
          {addingCol ? (
            <NameForm label="Name of the new column" placeholder="Column name" className="tda-section-form"
                      onSave={(n) => { setAddingCol(false); on.addColumn(n); }} onCancel={() => setAddingCol(false)} />
          ) : (
            <button type="button" className="tdb-add-col" disabled={busy}
                    onClick={() => setAddingCol(true)}>+ Add column</button>
          )}
        </div>
      </div>
      {drag && (
        <div className={`tdb-ghost${drag.type === 'col' ? ' is-col' : ''}`} aria-hidden="true"
             style={{ left: drag.x, top: drag.y, width: drag.width }}>
          <span className="tdb-grip"><Icon name="grip" /></span>
          <span className="tdb-ghost-title">{drag.title}</span>
        </div>
      )}
    </div>
    </div>
  );
}

/** A column's place among the named columns other than the one being dragged. */
function namedOthersIndex(named, dragId, id) {
  return named.filter((c) => c.section.id !== dragId).findIndex((c) => c.section.id === id);
}
function isLastDrop(named, dragId, id, index) {
  const others = named.filter((c) => c.section.id !== dragId);
  return index >= others.length && others.length > 0 && others[others.length - 1].section.id === id;
}
